import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'

import { getWalletInfoQueryKey, type GetWalletInfoResponse } from '@/shared/api'
import { useExperiment, useExperimentsReady } from '@/shared/hooks/use-experiment'
import { currencyParser, currencyParserToNumber } from '@/shared/lib/currency-parser'
import { AppsflyerEvent, sendAppsflyer } from '@/shared/metrics'
import { Button, Chips, Icon } from '@/shared/ui'

import { type TFormValues, useMetrics } from '../../model'

import styles from './driver-recharge-amount.module.scss'

type TDriverRechargeAmountProps = {
    openModal: () => void
    ref: React.Ref<HTMLDivElement>
}

export const DriverRechargeAmount = ({
    openModal,
    ref,
}: TDriverRechargeAmountProps): React.ReactNode => {
    const { financeTopupSumClickEvent, financeTopupTagSumClickEvent } = useMetrics({
        formMethods: useFormContext<TFormValues>(),
    })
    const growthExperiment = useExperiment('driver_recharge_growth')
    const experimentsReady = useExperimentsReady()

    const queryClient = useQueryClient()
    const data = queryClient.getQueryData<GetWalletInfoResponse>(getWalletInfoQueryKey())
    const { watch, setValue } = useFormContext<TFormValues>()

    return (
        <div ref={ref} className={styles['driver-recharge-amount']} id='amount-tooltip'>
            <div className={clsx(styles['driver-recharge-amount__edit'])}>
                <button
                    className={styles['driver-recharge-amount__text']}
                    type='button'
                    onClick={() => {
                        sendAppsflyer(
                            AppsflyerEvent.finance_topup_sum_click,
                            financeTopupSumClickEvent('placeholder'),
                        )
                        openModal()
                    }}
                >
                    <span
                        className={styles['driver-recharge-amount__text-value']}
                        data-test-id='driver-recharge-amount'
                    >
                        {currencyParser(watch('value'))}
                    </span>
                    <span className={styles['driver-recharge-amount__text-currency']}>₽</span>
                </button>
                <Button
                    className={styles['driver-recharge-amount__icon']}
                    size='small'
                    type='button'
                    variant='secondary'
                    onClick={() => {
                        sendAppsflyer(
                            AppsflyerEvent.finance_topup_sum_click,
                            financeTopupSumClickEvent('button'),
                        )
                        openModal()
                    }}
                >
                    <Icon icon='edit' size={20} />
                </Button>
            </div>
            <div className={clsx(styles['driver-recharge-amount__chips'])}>
                {(growthExperiment === 'A' || growthExperiment === 'C') && experimentsReady && (
                    <span className={styles['driver-recharge-amount__chips--subtitle']}>
                        Примерно хватит на S/k заказов
                    </span>
                )}
                <Chips
                    chips={
                        data?.topup.presets.map((preset) => ({
                            value: currencyParser(preset) + ' ₽',
                            subTitle:
                                (
                                    (growthExperiment === 'A' || growthExperiment === 'C') &&
                                    experimentsReady
                                ) ?
                                    `~s/k заказа`
                                :   undefined,
                        })) ?? []
                    }
                    setValue={(value) => {
                        const numberValue = currencyParserToNumber(
                            value.toString().replace(' ₽', ''),
                        )

                        sendAppsflyer(
                            AppsflyerEvent.finance_topup_tag_sum_click,
                            financeTopupTagSumClickEvent(numberValue / 100),
                        )
                        setValue('value', numberValue.toString())
                    }}
                />
                {(growthExperiment === 'B' || growthExperiment === 'C') && experimentsReady && (
                    <div className={styles['driver-recharge-amount__chips--example']}>
                        <div>
                            <Icon
                                color='var(--extensions-text-and-icon-always-dark)'
                                icon='money'
                                size={24}
                            />
                        </div>
                        <span>
                            Прогнозируемый доход при пополнении баланса составит{' '}
                            <span
                                className={styles['driver-recharge-amount__chips--example--bold']}
                            >
                                S*k ₽
                            </span>
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}
